import React from "react";
import { Button, makeStyles, tokens } from "@fluentui/react-components";
import { ArrowClockwiseRegular, ArrowResetRegular } from "@fluentui/react-icons";
import FilterControl from "./FilterControl";
import type { DashboardFilter, FilterStats } from "../api/types";

// ── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  /** Outer container: filters left, reset button right. */
  bar: {
    display: "flex",
    alignItems: "flex-start",
    padding: "12px 16px",
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  /** Wrapping area for filter chips. */
  filtersArea: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
  },
  /** Right-side action buttons area. */
  actionsArea: {
    display: "flex",
    gap: "4px",
    flexShrink: 0,
    marginLeft: "12px",
    alignSelf: "flex-start",
  },
  actionButton: {
    borderRadius: "20px",
    whiteSpace: "nowrap",
  },
});

// ── Props ────────────────────────────────────────────────────────────────────

export interface DashboardFilterBarProps {
  dashboardId: string;
  filters: DashboardFilter[];
  filterValues: Map<string, string | string[] | boolean | Record<string, unknown> | null>;
  onFilterChange: (variable: string, value: any) => void;
  /**
   * Called when a filter auto-applies a default on mount (e.g. date range
   * "Last 12 months"). This value feeds into chart queries but does NOT
   * mark the filter as "modified" — no reset (X) icon appears.
   */
  onFilterDefault?: (variable: string, value: any) => void;
  onFilterReset: (variable: string) => void;
  onResetAll: () => void;
  onFilterReady: (filterId: string) => void;
  /** Clears the dashboard cache and reloads all chart data from the server. */
  onRefreshData?: () => void;
  shareToken?: string;
  sharedDashboardId?: string;
}

/**
 * Shared filter bar used by both Dashboard and ChartEditPage.
 *
 * Layout: filters wrap freely on the left; "Reset All" stays pinned
 * to the right, aligned with the first row of chips.
 */
export const DashboardFilterBar: React.FC<DashboardFilterBarProps> = ({
  dashboardId,
  filters,
  filterValues,
  onFilterChange,
  onFilterDefault,
  onFilterReset,
  onResetAll,
  onFilterReady,
  onRefreshData,
  shareToken,
  sharedDashboardId,
}) => {
  const styles = useStyles();

  const hasAnyModified = filterValues.size > 0;

  const sortedFilters = React.useMemo(
    () => [...filters].sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0)),
    [filters],
  );

  return (
    <div className={styles.bar}>
      <div className={styles.filtersArea}>
        {sortedFilters.map((filter) => (
          <FilterControl
            key={filter.id}
            dashboardId={dashboardId}
            filterId={filter.id}
            filterType={filter.type as "categorical" | "date_range" | "numeric" | "boolean"}
            filterStats={(filter.filterStats ?? null) as FilterStats | null}
            valueType={filter.valueType ?? null}
            label={filter.name}
            variable={filter.variable}
            defaultValue={filterValues.get(filter.variable) ?? undefined}
            isModified={filterValues.has(filter.variable)}
            onSelectionChange={(value) => onFilterChange(filter.variable, value)}
            onDefaultApplied={onFilterDefault}
            onReset={() => onFilterReset(filter.variable)}
            onReady={onFilterReady}
            shareToken={shareToken}
            sharedDashboardId={sharedDashboardId}
          />
        ))}
      </div>

      <div className={styles.actionsArea}>
        {hasAnyModified && (
          <Button
            appearance="subtle"
            size="small"
            className={styles.actionButton}
            icon={<ArrowResetRegular />}
            onClick={onResetAll}
          >
            Reset
          </Button>
        )}
        {onRefreshData && (
          <Button
            appearance="subtle"
            size="small"
            className={styles.actionButton}
            icon={<ArrowClockwiseRegular />}
            onClick={onRefreshData}
            title="Clear cache and reload all data from server"
          >
            Refresh
          </Button>
        )}
      </div>
    </div>
  );
};